import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

// Form schema
const createPageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title is too long'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(100, 'Slug is too long')
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/,
      'Slug must contain only lowercase letters, numbers, and hyphens, and cannot start or end with a hyphen'
    ),
})

type CreatePageFormData = z.infer<typeof createPageSchema>

interface CreateCustomPageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string
  onSuccess: (pageId: string) => void
}

// Generate slug from title
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100)
}

export function CreateCustomPageDialog({
  open,
  onOpenChange,
  accountId,
  onSuccess,
}: CreateCustomPageDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const form = useForm<CreatePageFormData>({
    resolver: zodResolver(createPageSchema),
    defaultValues: {
      title: '',
      slug: '',
    },
  })

  const createMutation = useMutation({
    mutationFn: async (data: CreatePageFormData) => {
      const { data: page, error } = await supabase
        .from('custom_pages')
        .insert({
          account_id: accountId,
          title: data.title,
          slug: data.slug,
          content: '', // Empty content to start
          is_published: false,
          show_in_navigation: false,
          navigation_order: 0,
        })
        .select()
        .single()

      if (error) {
        if (error.code === '23505') {
          throw new Error('A page with this slug already exists')
        }
        throw error
      }

      return page
    },
    onSuccess: (page) => {
      queryClient.invalidateQueries({ queryKey: ['custom-pages'] })
      form.reset()
      onSuccess(page.id)
    },
    onError: (error) => {
      toast({
        title: 'Error creating page',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value
    form.setValue('title', title)

    // Auto-generate slug if slug field is empty or matches previous auto-generated value
    const currentSlug = form.getValues('slug')
    const previousTitle = form.getValues('title')
    const previousAutoSlug = generateSlug(previousTitle)

    if (!currentSlug || currentSlug === previousAutoSlug) {
      form.setValue('slug', generateSlug(title))
    }
  }

  const onSubmit = (data: CreatePageFormData) => {
    createMutation.mutate(data)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Page</DialogTitle>
          <DialogDescription>
            Create a new custom page for your website. You can edit the content
            after creation.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Page Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Privacy Policy"
                      {...field}
                      onChange={handleTitleChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL Slug</FormLabel>
                  <FormControl>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground mr-1">
                        /p/
                      </span>
                      <Input placeholder="privacy-policy" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>
                    The URL path for this page (e.g., /p/privacy-policy)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create Page
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
